"use client";

import { useEffect, type ReactNode } from "react";
import { realtimeConnectionManager, type RealtimeEvent } from "./connection-manager";
import { useConnectionStore } from "./connection-store";

export function useConnection() {
  const connectionState = useConnectionStore((state) => state.connectionState);
  const connecting = useConnectionStore((state) => state.connecting);
  const connected = useConnectionStore((state) => state.connected);
  const disconnected = useConnectionStore((state) => state.disconnected);
  const reconnecting = useConnectionStore((state) => state.reconnecting);
  const latency = useConnectionStore((state) => state.latency);
  const serverTime = useConnectionStore((state) => state.serverTime);
  const reconnectCount = useConnectionStore((state) => state.reconnectCount);
  const connectionDurationMs = useConnectionStore((state) => state.connectionDurationMs);
  const apiConnected = useConnectionStore((state) => state.apiConnected);
  const signalrConnected = useConnectionStore((state) => state.signalrConnected);

  return {
    connectionState,
    connecting,
    connected,
    disconnected,
    reconnecting,
    latency,
    serverTime,
    reconnectCount,
    connectionDurationMs,
    apiConnected,
    signalrConnected,
  };
}

export function useDeviceEvents<T extends RealtimeEvent>(handler: (event: T) => void) {
  useEffect(() => {
    const unsubscribe = realtimeConnectionManager.subscribe("device.online", handler as (event: RealtimeEvent) => void);
    const unsubscribeOffline = realtimeConnectionManager.subscribe("device.offline", handler as (event: RealtimeEvent) => void);

    return () => {
      unsubscribe();
      unsubscribeOffline();
    };
  }, [handler]);
}

export function useGpsEvents<T extends RealtimeEvent>(handler: (event: T) => void) {
  useEffect(() => {
    const unsubscribe = realtimeConnectionManager.subscribe("gps.updated", handler as (event: RealtimeEvent) => void);

    return () => {
      unsubscribe();
    };
  }, [handler]);
}

export function ConnectionStatusIndicator() {
  const { connectionState, latency, reconnectCount, connectionDurationMs } = useConnection();

  const statusText =
    connectionState === "connected"
      ? "Connected"
      : connectionState === "reconnecting"
        ? "Reconnecting"
        : connectionState === "connecting"
          ? "Connecting"
          : "Offline";

  const statusColor =
    connectionState === "connected"
      ? "bg-emerald-500"
      : connectionState === "reconnecting"
        ? "bg-amber-500"
        : "bg-red-500";

  return (
    <div className="fixed bottom-4 left-4 z-50 flex items-center gap-2 rounded-full border border-zinc-200 bg-white/90 px-3 py-1.5 text-[11px] shadow-sm backdrop-blur-sm dark:border-zinc-800 dark:bg-zinc-900/90">
      <span className={`h-2.5 w-2.5 rounded-full ${statusColor}`} />
      <span className="font-medium text-zinc-700 dark:text-zinc-200">{statusText}</span>
      <span className="text-zinc-400">•</span>
      <span className="text-zinc-500 dark:text-zinc-400">{latency}ms</span>
      {reconnectCount > 0 && (
        <>
          <span className="text-zinc-400">•</span>
          <span className="text-zinc-500 dark:text-zinc-400">{reconnectCount}</span>
        </>
      )}
      {connectionState === "connected" && (
        <>
          <span className="text-zinc-400">•</span>
          <span className="text-zinc-500 dark:text-zinc-400">
            {Math.max(0, Math.round(connectionDurationMs / 1000))}s
          </span>
        </>
      )}
    </div>
  );
}

export function RealtimeProvider({ children }: { children: ReactNode }) {
  const isConnected = useConnectionStore((state) => state.connected);

  useEffect(() => {
    void realtimeConnectionManager.start();

    return () => {
      void realtimeConnectionManager.stop();
    };
  }, []);

  useEffect(() => {
    const interval = window.setInterval(() => {
      const store = useConnectionStore.getState();
      const connectedAt = store.lastConnectedAt;
      if (store.connected && connectedAt) {
        store.setConnectionDuration(Date.now() - connectedAt);
      }
    }, 1000);

    return () => {
      window.clearInterval(interval);
    };
  }, [isConnected]);

  return (
    <>
      {children}
      <ConnectionStatusIndicator />
    </>
  );
}
